import { useState, useEffect, useRef } from 'react';
import { X, Loader2, GitBranch, ChevronDown } from 'lucide-react';

interface ExistingEnvironment {
  id: string;
  name: string;
  inherits_from?: string;
}

interface CreateEnvironmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; slug: string; inherits_from?: string }) => Promise<void>;
  existingSlugs: string[];
  existingEnvironments?: ExistingEnvironment[];
}

/**
 * Modal for creating a new environment
 */
export function CreateEnvironmentModal({
  isOpen,
  onClose,
  onCreate,
  existingSlugs,
  existingEnvironments = [],
}: CreateEnvironmentModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [parentEnvId, setParentEnvId] = useState<string>('');
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setName('');
      setSlug('');
      setParentEnvId('');
      setIsSlugManuallyEdited(false);
      setError(null);
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!isSlugManuallyEdited && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(generatedSlug);
    }
  }, [name, isSlugManuallyEdited]);

  const validateSlug = (s: string): string | null => {
    if (!s.trim()) {
      return 'Slug is required';
    }
    if (!/^[a-z][a-z0-9-]*[a-z0-9]$/.test(s) && s.length > 1) {
      return 'Slug must start with a letter, end with a letter or number, and contain only lowercase letters, numbers, and hyphens';
    }
    if (s.length === 1 && !/^[a-z]$/.test(s)) {
      return 'Slug must start with a letter';
    }
    if (existingSlugs.includes(s)) {
      return 'An environment with this slug already exists';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const slugError = validateSlug(slug);
    if (slugError) {
      setError(slugError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onCreate({ 
        name: name.trim(), 
        slug: slug.trim(),
        inherits_from: parentEnvId || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create environment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedParentEnv = existingEnvironments.find(e => e.id === parentEnvId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={isSubmitting ? undefined : onClose}
      />

      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Create Environment</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-5">
            <div>
              <label
                htmlFor="env-name"
                className="block text-sm font-medium text-secondary-foreground mb-1.5"
              >
                Name <span className="text-destructive">*</span>
              </label>
              <input
                ref={nameInputRef}
                id="env-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g., Production, Staging, Development"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm text-foreground placeholder:text-muted-foreground transition-colors"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label
                htmlFor="env-slug"
                className="block text-sm font-medium text-secondary-foreground mb-1.5"
              >
                Slug <span className="text-destructive">*</span>
              </label>
              <input
                id="env-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value.toLowerCase());
                  setIsSlugManuallyEdited(true);
                  setError(null);
                }}
                placeholder="e.g., production, staging, dev"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary font-mono text-sm text-foreground placeholder:text-muted-foreground transition-colors"
                disabled={isSubmitting}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Used in URLs and CLI commands. Lowercase letters, numbers, and hyphens only.
              </p>
            </div>

            <div>
              <label
                htmlFor="env-parent"
                className="block text-sm font-medium text-secondary-foreground mb-1.5"
              >
                <span className="flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                  Parent Environment
                </span>
              </label>
              <div className="relative">
                <select
                  id="env-parent"
                  value={parentEnvId}
                  onChange={(e) => setParentEnvId(e.target.value)}
                  disabled={isSubmitting || existingEnvironments.length === 0}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm text-foreground transition-colors appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">None (standalone environment)</option>
                  {existingEnvironments.map((env) => (
                    <option key={env.id} value={env.id}>
                      {env.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
              {selectedParentEnv && (
                <div className="mt-2 p-2.5 bg-success/10 border border-success/20 rounded-lg">
                  <p className="text-xs text-success flex items-center gap-1.5">
                    <GitBranch className="h-3 w-3" />
                    Secrets from <span className="font-medium">{selectedParentEnv.name}</span> will be inherited
                  </p>
                </div>
              )}
              {existingEnvironments.length === 0 && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  No existing environments to inherit from.
                </p>
              )}
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">Quick presets:</p>
              <div className="flex flex-wrap gap-2">
                {['Development', 'Staging', 'Production', 'Testing', 'Preview'].map(
                  (preset) => {
                    const presetSlug = preset.toLowerCase();
                    const isDisabled = existingSlugs.includes(presetSlug);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setName(preset);
                          setSlug(presetSlug);
                          setIsSlugManuallyEdited(false);
                        }}
                        disabled={isDisabled || isSubmitting}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                          isDisabled
                            ? 'bg-muted/50 text-muted-foreground/50 border-border cursor-not-allowed'
                            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground hover:border-border/80'
                        }`}
                      >
                        {preset}
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-5 border-t border-border bg-card/50 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name || !slug}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground rounded-lg transition-colors flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Environment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
